import {
  useOrganisationTaxRates,
  useOrganisationVatTreatments,
  useSetOrganisationVatTreatment,
  useSetOrganisationVatTreatmentDefaults,
} from '@my/directory-api/hooks';
import {
  vatTreatmentLineLabel,
  type OrganisationTaxRate,
  type VatTreatmentLine,
} from '@my/directory-api';
import { findCountry } from '@my/platform/options';
import { ErrorBanner, FormSection, Loading, showToast } from '@my/ui';
import { Info, Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface VatTreatmentSectionProps {
  /** Owning organisation id; the section stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Accounting — VAT Treatment: which of the organisation's tax schemas a sale
 * is invoiced with, by where the CUSTOMER is registered for VAT and where the
 * goods go. Sits between Tax Schemas (what the schemas are) and Categories.
 *
 * <p>
 * The lines are fixed by the organisation's registered country — four for an
 * EU organisation, three for a UK (GB / XI) one — and live in the Directory
 * (`VatTreatmentRules`). Only the choice per line is the organisation's: a
 * dropdown that saves on change. "Set defaults" fills the unset lines with the
 * schema of the expected type, adding one (Outside scope, say) when missing.
 * The invoice builder defaults each line's VAT from here.
 * </p>
 */
export function VatTreatmentSection({ organisationId }: VatTreatmentSectionProps) {
  const { t } = useTranslation('organisation');
  const matrixQuery = useOrganisationVatTreatments(organisationId);
  const ratesQuery = useOrganisationTaxRates(organisationId);
  const setMutation = useSetOrganisationVatTreatment();
  const defaultsMutation = useSetOrganisationVatTreatmentDefaults();

  const matrix = matrixQuery.data;
  const home = matrix?.homeCountryIsoCode ?? '';
  const homeName = findCountry(home)?.name ?? home;
  const rates = ratesQuery.data ?? [];
  const busy = setMutation.isPending || defaultsMutation.isPending;

  // Every line should always point at a schema. An organisation whose matrix
  // is still wholly empty (it seeded its tax rates before the matrix existed,
  // or added them by hand) gets the defaults filled — and SAVED — the first
  // time this section loads, once per visit. Seeding tax rates already does
  // this on the server; this covers everyone who seeded before.
  const autoFilled = useRef(false);
  useEffect(() => {
    if (autoFilled.current || !organisationId || !matrix?.regime || !ratesQuery.isSuccess) return;
    if (matrix.lines.length === 0 || matrix.lines.some((l) => l.taxRateId)) return;
    autoFilled.current = true;
    defaultsMutation.mutate(
      { organisationId },
      {
        onSuccess: () => showToast({ type: 'success', message: t('vatTreatment.autoFilled') }),
        onError: (err) => showToast({ type: 'error', message: err.message || t('vatTreatment.saveFailed') }),
      },
    );
    // `defaultsMutation` / `t` are stable enough; the guard ref stops re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, matrix, ratesQuery.isSuccess]);

  const choose = async (line: VatTreatmentLine, taxRateId: string | null) => {
    if (!organisationId) return;
    try {
      await setMutation.mutateAsync({ organisationId, ruleKey: line.ruleKey, taxRateId });
      showToast({ type: 'success', message: t('vatTreatment.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('vatTreatment.saveFailed'),
      });
    }
  };

  const setDefaults = async () => {
    if (!organisationId) return;
    try {
      await defaultsMutation.mutateAsync({ organisationId });
      showToast({ type: 'success', message: t('vatTreatment.defaultsSet') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('vatTreatment.saveFailed'),
      });
    }
  };

  const regimeKey = matrix?.regime === 'Uk' ? 'uk' : 'eu';
  // Line keys come from the server, so these lookups are built at run time —
  // past what the typed key union can check. The EN file has every one.
  const tLine = (key: string, vars?: Record<string, string>) =>
    String(t(key as never, { country: home, ...vars } as never));
  const headerAction =
    matrix?.regime != null ? (
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">
          {t('vatTreatment.registeredIn', { country: homeName })}
        </span>
        <button
          type="button"
          onClick={() => void setDefaults()}
          disabled={!organisationId || busy}
          title={t('vatTreatment.setDefaultsTitle')}
          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('vatTreatment.setDefaults')}
        </button>
      </div>
    ) : null;

  return (
    <FormSection title={t('vatTreatment.title')} columns={1} actions={headerAction}>
      {(matrixQuery.isPending || ratesQuery.isPending) && <Loading label={t('vatTreatment.loading')} />}
      {matrixQuery.isError && (
        <ErrorBanner title={t('vatTreatment.loadFailed')} message={matrixQuery.error.message} />
      )}

      {matrix && matrix.regime == null && (
        <p className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-sm text-slate-600">
          {t('vatTreatment.noRegime', { country: homeName || '—' })}
        </p>
      )}

      {matrix && matrix.regime != null && ratesQuery.isSuccess && (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-md">
            <div className="overflow-auto">
              <table className="w-full min-w-[820px] table-auto text-xs">
                <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                  <tr>
                    <th className="w-14 px-3 py-2 text-left font-semibold">#</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('vatTreatment.table.customer')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('vatTreatment.table.route')}</th>
                    <th className="w-64 px-3 py-2 text-left font-semibold">{t('vatTreatment.table.treatment')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('vatTreatment.table.basis')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {matrix.lines.map((line) => (
                    <tr key={line.ruleKey} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-400">
                        {vatTreatmentLineLabel(matrix, line.ruleKey)}
                      </td>
                      <td className="px-3 py-1.5 font-semibold text-slate-800">
                        {tLine(`vatTreatment.${regimeKey}.customer.${line.customer}`)}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700">
                        {tLine(`vatTreatment.${regimeKey}.route.${line.route}`)}
                      </td>
                      <td className="px-3 py-1">
                        <TreatmentSelect
                          line={line}
                          rates={rates}
                          disabled={!organisationId || busy}
                          unsetLabel={t('vatTreatment.unset')}
                          unsetHint={t('vatTreatment.unsetHint')}
                          checkHint={tLine(`vatTreatment.checkHint.${line.suggestedTaxType}`, {
                            line: vatTreatmentLineLabel(matrix, line.ruleKey),
                          })}
                          onChange={(id) => void choose(line, id)}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-slate-500">
                        {tLine(`vatTreatment.basis.${line.ruleKey}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" />
            {tLine(`vatTreatment.${regimeKey}.note`)}
          </p>
        </div>
      )}
    </FormSection>
  );
}

/**
 * Coloured by whether the line looks RIGHT, not by VAT type — the name in the
 * box already says the type, and a red "Standard rate" read as an error.
 *  - set: the RATE is what the line calls for — VAT charged on the Standard
 *    line, 0% on the zero / reverse-charge / outside-scope lines. Which 0%
 *    schema the organisation uses for a 0% line is its call (user's rule).
 *  - check: the rate is off — 0% on the Standard line, VAT on a 0% line →
 *    amber, with what the line normally uses on hover;
 *  - not set: nothing chosen → dashed amber; invoices fall back to Standard.
 */
const STATE_CLASS = {
  set: 'border-slate-300 bg-white text-slate-800',
  check: 'border-amber-300 bg-amber-50 text-amber-800',
  unset: 'border-dashed border-amber-300 bg-white text-amber-700',
} as const;

/**
 * The line's tax schema as a pill-shaped select. Offers the organisation's
 * active SALES schemas; one already chosen that has since gone inactive is
 * kept visible rather than silently dropped.
 */
function TreatmentSelect({
  line,
  rates,
  disabled,
  unsetLabel,
  unsetHint,
  checkHint,
  onChange,
}: {
  line: VatTreatmentLine;
  rates: OrganisationTaxRate[];
  disabled: boolean;
  unsetLabel: string;
  unsetHint: string;
  /** "IE-4 is normally 0% (Outside scope) — check this is right." */
  checkHint: string;
  onChange: (taxRateId: string | null) => void;
}) {
  const current = rates.find((r) => r.id === line.taxRateId) ?? null;
  const offered = rates
    .filter((r) => r.invoiceType !== 'Purchases' && (r.status === 'Active' || r.id === line.taxRateId))
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const chargesVat = line.suggestedTaxType === 'Standard';
  const state: keyof typeof STATE_CLASS = !current
    ? 'unset'
    : chargesVat === current.taxPercentage > 0
      ? 'set'
      : 'check';

  return (
    <select
      aria-label={line.ruleKey}
      value={line.taxRateId ?? ''}
      disabled={disabled}
      title={state === 'unset' ? unsetHint : state === 'check' ? checkHint : undefined}
      onChange={(e) => onChange(e.target.value || null)}
      className={`h-7 w-full rounded-full border px-2.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 ${STATE_CLASS[state]}`}
    >
      <option value="">{unsetLabel}</option>
      {offered.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name} ({formatPct(r.taxPercentage)}%)
        </option>
      ))}
    </select>
  );
}

function formatPct(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
